import {
    Button,
    Combobox,
    DialogTrigger,
    makeStyles,
    Option,
    OptionGroup,
    OptionOnSelectData,
    SelectionEvents,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDetailedWorkspace, useSaveDetailedWorkspace } from '../../../api/report/report';
import { useFormView } from '../../../contexts/useFormView';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useHiddenComboboxCheckIconStyles } from '../../../hooks/useHiddenComboboxCheckIconStyles';
import { usePreventNavigation } from '../../../hooks/usePreventNavigation';
import { DetailedWorkspaceDto, TaxReturnItemType, TaxReturnTOCItem } from '../../../model';
import { buildSelectedStatement, IFormViewerMethods, isStatementEmpty } from '../../../utils/DetailedWorkspaceUtils';
import { focusElementAfterFormModal } from '../../../utils/PdfViewerUtils';
import { TOTAL_COLUMN_ID } from '../../../utils/ReportUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import ModalV9 from '../../common/ModalV9';
import { useVirtualizedCombobox, VirtualizedOptionList } from '../../common/VirtualizedOptionList';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import ColumnTableFormViewer from './ColumnTableFormViewer';
import EmptyFormViewer from './EmptyFormViewer';
import RowTableFormViewer from './RowTableFormViewer';
import TextboxFormViewer from './TextboxFormViewer';

export interface ITableFormModalProps {
    isOpen: boolean;
    isAdd: boolean;
    onClose: () => void;
    detailedWorkspace: DetailedWorkspaceDto | null | undefined;
}

const useStyles = makeStyles({
    modalContent: {
        height: '500px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        overflow: 'hidden',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '500px',
        width: '100%',
        height: '100%',
        flex: '1 1 auto',
        overflow: 'hidden',
    },
    bodyWrapper: {
        height: '100%',
        width: '100%',
        flex: '1 1 auto',
        overflow: 'hidden',
        display: 'flex',
    },
    subTitle: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
    loadingSpinnerContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

const TableFormModal: React.FC<ITableFormModalProps> = ({ isOpen, onClose, detailedWorkspace, isAdd }) => {
    const { modalContent, tableContainer, bodyWrapper, subTitle, loadingSpinnerContainer } = useStyles();
    const { hidden: hiddenCheckIcon } = useHiddenComboboxCheckIconStyles();
    const { t } = useTranslation();
    const { mutate: saveDetailedWorkspace, isLoading: isSaving } = useSaveDetailedWorkspace();
    const { mutate: deleteDetailedWorkspace } = useDeleteDetailedWorkspace();
    const {
        returnKey,
        selectedStatement,
        setSelectedStatement,
        currentTOCItemState,
        tocData,
        loadStatementDataForModal,
        loadTocData,
        isLoadingDetailedWorkspace,
        elementToFocusAfterFormModal,
        currentWhitePaperReport,
    } = useFormView();
    const formViewerRef = React.useRef<IFormViewerMethods>(null);
    const comboboxInputRef = React.useRef<HTMLInputElement>(null);
    const addStatementHeaderRef = React.useRef<HTMLParagraphElement>(null);
    const { displayFeedbackMessage } = useFeedbackMessages();
    const [pendingTocItem, setPendingTocItem] = useState<TaxReturnTOCItem | null>(null);
    const [dataVersion, setDataVersion] = useState(0);

    const emptyDetailedWorkspaceItems = tocData?.itemsToAdd ?? [];

    const [filterText, setFilterText] = useState('');
    const [comboboxOpen, setComboboxOpen] = useState(false);
    const [hasUserEditedCombobox, setHasUserEditedCombobox] = useState(false);
    const filteredDetailedWorkspaceItems = useMemo(() => {
        if (!filterText.trim()) return emptyDetailedWorkspaceItems;
        const lower = filterText.toLowerCase();
        return emptyDetailedWorkspaceItems?.filter((item) => (item?.title ?? '').toLowerCase().includes(lower)) ?? [];
    }, [emptyDetailedWorkspaceItems, filterText]);
    const { functionalAccess } = useFunctionalAccess();
    const [isDirty, setIsDirty] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const currentColumnId =
        currentWhitePaperReport?.columns?.find((column) => column.columnId === selectedStatement?.entity?.id)?.originalColumnId ||
        currentWhitePaperReport?.columns?.[0]?.originalColumnId;
    const isConsolidated = selectedStatement?.isConsolidated;
    const shouldDisableRowStatement =
        selectedStatement?.taxReturnItemType === TaxReturnItemType.CalculationDetailStatementRow &&
        isConsolidated &&
        currentColumnId === TOTAL_COLUMN_ID;

    const { blocker, setShouldBlockNavigation } = usePreventNavigation();

    // Show confirmation dialog when navigation is blocked
    useEffect(() => {
        if (blocker.state === 'blocked' && isDirty && !showConfirmationDialog && !isSaving) {
            setShowConfirmationDialog(true);
        }
    }, [blocker.state, showConfirmationDialog, isSaving, isDirty]);

    // Reset dirty state and set selected statement when modal opens
    useEffect(() => {
        if (isOpen && !isAdd) {
            setIsDirty(false);
            setShouldBlockNavigation(false);
            if (!selectedStatement) {
                setSelectedStatement(currentTOCItemState);
            }
        }
    }, [isOpen, currentTOCItemState, selectedStatement]);

    // Reset combobox state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setComboboxOpen(false);
            setFilterText('');
            setHasUserEditedCombobox(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && isAdd) {
            setTimeout(() => addStatementHeaderRef.current?.focus(), 0);
        }
    }, [isOpen, isAdd]);

    // Increment dataVersion whenever detailedWorkspace changes to refresh form viewers
    useEffect(() => {
        if (detailedWorkspace) {
            setDataVersion((prev) => prev + 1);
        }
    }, [detailedWorkspace]);

    // Handle escape key to show confirmation dialog when there are unsaved changes
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            const hasUnsavedEditChanges = formViewerRef.current?.hasUnsavedEditChanges?.() ?? false;
            if (event.key === 'Escape' && isOpen && (isDirty || hasUnsavedEditChanges) && !showConfirmationDialog) {
                event.preventDefault();
                event.stopPropagation();
                setShowConfirmationDialog(true);
            }
        };

        document.addEventListener('keydown', handleEscapeKey, true);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey, true);
        };
    }, [isOpen, isDirty, showConfirmationDialog]);

    const dataName = selectedStatement?.dataName ?? '';
    const tocItemTitle = selectedStatement?.tocItemTitle ?? '';
    const taxReturnItemType = selectedStatement?.taxReturnItemType ?? undefined;
    const selectedDetailedWorkspaceTitle = useMemo(
        () => emptyDetailedWorkspaceItems?.find((item) => item?.id === selectedStatement?.id)?.title ?? '',
        [emptyDetailedWorkspaceItems, selectedStatement]
    );
    // Show selected value until user starts editing; then show filterText (so box can stay empty when they clear it)
    const comboboxDisplayValue = hasUserEditedCombobox ? filterText : selectedDetailedWorkspaceTitle;

    const onSaveChanges = () => {
        const updatedDetailedWorkspace = formViewerRef.current?.getUpdatedDetailedWorkspace();
        if (!updatedDetailedWorkspace || !dataName) return;

        const isStatementEmptyFlag = isStatementEmpty(updatedDetailedWorkspace, taxReturnItemType);

        if (!isAdd && isStatementEmptyFlag) {
            setShowDeleteConfirmationDialog(true);
            return;
        }

        saveDetailedWorkspace(
            {
                data: {
                    taxReturnItemKey: returnKey,
                    dataName: dataName,
                    activeColumnIndex: selectedStatement?.entity?.index ?? 0,
                    detailedWorkspace: updatedDetailedWorkspace,
                    taxReturnItemType,
                },
            },
            { onSuccess: () => onSaveSuccess(false) }
        );
    };

    const handleDeleteStatement = () => {
        deleteDetailedWorkspace(
            {
                data: {
                    taxReturnItemKey: returnKey,
                    dataName: dataName,
                    activeColumnIndex: selectedStatement?.entity?.index ?? 0,
                    detailedWorkspace: detailedWorkspace ?? undefined,
                    taxReturnItemType,
                },
            },
            {
                onSuccess: () => {
                    setShowDeleteConfirmationDialog(false);
                    onSaveSuccess(true);
                },
            }
        );
    };

    const onConfirmDelete = () => {
        handleDeleteStatement();
    };

    const cleanUpStateAndClose = () => {
        formViewerRef.current?.cleanUpState?.();
        setIsDirty(false);
        setShouldBlockNavigation(false);
        setShowConfirmationDialog(false);

        // If we're changing to a different statement, navigate to it
        if (pendingTocItem) {
            const tocItem = pendingTocItem;
            setPendingTocItem(null);
            setSelectedStatement(buildSelectedStatement(tocItem, selectedStatement?.entity));
            loadStatementDataForModal([tocItem], selectedStatement?.entity);
            return;
        }

        if (elementToFocusAfterFormModal) {
            focusElementAfterFormModal(elementToFocusAfterFormModal);
        }

        onClose();
    };

    const handleCloseRequest = () => {
        const hasUnsavedEditChanges = formViewerRef.current?.hasUnsavedEditChanges?.() ?? false;
        if (isDirty || hasUnsavedEditChanges) {
            setShowConfirmationDialog(true);
        } else {
            cleanUpStateAndClose();
        }
    };

    const onSaveSuccess = (isDelete: boolean) => {
        if (!isDelete) displayFeedbackMessage({ type: 'success', message: t('statementSavedSuccessfully') });
        // Reload TOC data since saving detailed workspace changes can affect TOC (do not open statement modal)
        loadTocData(undefined, { skipGetFormData: true });
        cleanUpStateAndClose();
    };

    const onConfirmationCancel = () => {
        setShowConfirmationDialog(false);
        setPendingTocItem(null); // Clear pending navigation
        if (blocker.state === 'blocked') {
            blocker.reset();
        }

        // Restore focus to the form (e.g. textbox) after the Dialog closes and clears aria-hidden
        setTimeout(() => formViewerRef.current?.focus?.(), 0);
    };

    const onConfirmationSave = () => {
        setShowConfirmationDialog(false);
        onSaveChanges();
    };

    const onConfirmationDiscard = () => {
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
        cleanUpStateAndClose();
    };

    const handleDataChange = () => {
        setIsDirty(true);
        setShouldBlockNavigation(true);
    };

    const optionToSelectedOption = useCallback((item: TaxReturnTOCItem) => item?.id ?? '', []);
    const selectDetailedWorkspaceItem = useCallback(
        (_: SelectionEvents, data: OptionOnSelectData) => {
            if (data.optionValue) {
                setFilterText('');
                setComboboxOpen(false);
                setHasUserEditedCombobox(false);
                // Move focus out of the search box after selection
                setTimeout(() => comboboxInputRef.current?.blur(), 0);
                const hasUnsavedEditChanges = formViewerRef.current?.hasUnsavedEditChanges?.() ?? false;
                const tocItem = emptyDetailedWorkspaceItems?.find((item) => item?.id === data.optionValue);

                if (tocItem) {
                    // If there are unsaved changes, show confirmation dialog
                    if (isDirty || hasUnsavedEditChanges) {
                        setPendingTocItem(tocItem);
                        setShowConfirmationDialog(true);
                        return;
                    }

                    // No unsaved changes, navigate directly
                    setIsDirty(false);
                    setSelectedStatement(buildSelectedStatement(tocItem, currentTOCItemState?.entity));
                    loadStatementDataForModal([tocItem], currentTOCItemState?.entity);
                }
            }
        },
        [emptyDetailedWorkspaceItems, isDirty, currentTOCItemState?.entity, loadStatementDataForModal, setSelectedStatement]
    );

    const virtualizerConfig = useVirtualizedCombobox<TaxReturnTOCItem>({
        selectedOptions: [selectedStatement?.id ?? ''],
        matchingOptionsData: filteredDetailedWorkspaceItems ?? [],
        optionToSelectedOption,
        onOptionSelect: selectDetailedWorkspaceItem,
    });
    const handleComboboxOpenChange = useCallback(
        (e: Parameters<NonNullable<typeof virtualizerConfig.onOpenChange>>[0], data: { open: boolean }) => {
            virtualizerConfig.onOpenChange?.(e, data);
            if (!data.open) {
                setFilterText('');
                setComboboxOpen(false);
                setHasUserEditedCombobox(false);
            } else {
                const isInputClick = e?.target && comboboxInputRef.current === e.target;
                if (!isInputClick) {
                    setComboboxOpen(true);
                }
            }
        },
        [virtualizerConfig.onOpenChange]
    );

    const renderDetailedWorkspaceOption = useCallback(
        (item: TaxReturnTOCItem) => (
            <Option key={item?.id ?? ''} value={item?.id ?? ''} text={item?.title ?? ''} className={hiddenCheckIcon}>
                {item?.title}
            </Option>
        ),
        [hiddenCheckIcon]
    );

    const renderContent = () => {
        if (isLoadingDetailedWorkspace) {
            return (
                <div className={loadingSpinnerContainer}>
                    <LoadingSpinner id='loadingSpinner' label={t('loadingPleaseWait').toString()} />
                </div>
            );
        }

        switch (taxReturnItemType) {
            case TaxReturnItemType.CalculationDetailStatementColumn:
                return (
                    <ColumnTableFormViewer
                        key={`${selectedStatement?.id}-${dataVersion}`}
                        ref={formViewerRef}
                        detailedWorkspace={detailedWorkspace}
                        onDataChange={handleDataChange}
                    />
                );
            case TaxReturnItemType.CalculationDetailStatementText:
                return (
                    <TextboxFormViewer
                        key={`${selectedStatement?.id}-${dataVersion}`}
                        ref={formViewerRef}
                        detailedWorkspace={detailedWorkspace}
                        onDataChange={handleDataChange}
                    />
                );
            case TaxReturnItemType.CalculationDetailStatementRow:
                return (
                    <RowTableFormViewer
                        key={`${selectedStatement?.id}-${dataVersion}`}
                        ref={formViewerRef}
                        detailedWorkspace={detailedWorkspace}
                        disabled={shouldDisableRowStatement}
                        onDataChange={handleDataChange}
                        onDeleteAllRows={() => setShowDeleteConfirmationDialog(true)}
                    />
                );
            default:
                return <EmptyFormViewer isAdd={isAdd} />;
        }
    };

    const modalButtons = (
        <>
            <Button
                appearance='primary'
                aria-label={t('save').toString()}
                onClick={onSaveChanges}
                disabled={Boolean(!isDirty || shouldDisableRowStatement || isSaving)}
            >
                {isSaving ? t('working').toString() : t('save').toString()}
                {isSaving && <Spinner size='extra-tiny' />}
            </Button>
            <Button aria-label={t('cancel').toString()} onClick={handleCloseRequest}>
                {t('cancel').toString()}
            </Button>
        </>
    );

    const leftModalButton = isAdd ? undefined : (
        <Button
            disabled={shouldDisableRowStatement || functionalAccess?.amountDataEntryDeleteEnabled === false}
            aria-label={t('deleteStatement').toString()}
            onClick={() => setShowDeleteConfirmationDialog(true)}
        >
            {t('deleteStatement').toString()}
        </Button>
    );

    const editStatementHeader = (
        <div>
            {t('editStatement')}
            <p className={subTitle}>{tocItemTitle}</p>
        </div>
    );

    const showNoResults = filterText.trim() !== '' && filteredDetailedWorkspaceItems.length === 0;
    const noResultsText = showNoResults ? t('noOptionsFound', { searchText: filterText }) : '';
    const addStatementHeader = (
        <div>
            <p ref={addStatementHeaderRef} tabIndex={-1}>
                {t('addStatement')}
            </p>
            <Combobox
                ref={comboboxInputRef}
                id='detailed-workspace-combobox'
                data-testid='detailed-workspace-combobox'
                placeholder={t('selectLineItem').toString()}
                value={comboboxDisplayValue}
                selectedOptions={[selectedStatement?.id ?? '']}
                onOptionSelect={virtualizerConfig.onOptionSelect ?? selectDetailedWorkspaceItem}
                onChange={(e) => {
                    const value = e.target.value ?? '';
                    setFilterText(value);
                    setHasUserEditedCombobox(true);
                    // Keep dropdown open when typing or when user clears text so they see full list
                    setComboboxOpen(true);
                }}
                onOpenChange={handleComboboxOpenChange}
                open={comboboxOpen}
                freeform
                autoComplete='off'
                positioning='below'
                listbox={{
                    ref: virtualizerConfig.scrollRef,
                    className: virtualizerConfig.listboxClassName,
                }}
            >
                {showNoResults ? (
                    <Option key='no-results' value='' text={noResultsText.toString()} disabled className={hiddenCheckIcon}>
                        {noResultsText}
                    </Option>
                ) : (
                    <OptionGroup>
                        <VirtualizedOptionList
                            items={filteredDetailedWorkspaceItems}
                            renderOption={renderDetailedWorkspaceOption}
                            virtualizerConfig={virtualizerConfig}
                        />
                    </OptionGroup>
                )}
            </Combobox>
        </div>
    );

    const onCancelDelete = () => {
        setShowDeleteConfirmationDialog(false);
        document.getElementById('table-form-modal')?.focus();
    };

    return (
        <>
            <ModalV9
                isOpen={isOpen}
                id='table-form-modal'
                onClose={handleCloseRequest}
                headerContent={isAdd ? addStatementHeader : editStatementHeader}
                contentStylesProps={modalContent}
                bodyStylesProps={tableContainer}
                modalMaxHeightInPixels={900}
                modalMaxWidthInPixels={1200}
                showModalCloseButton={true}
                bodyContent={<div className={bodyWrapper}>{renderContent()}</div>}
                buttons={modalButtons}
                leftFooterElement={leftModalButton}
            />
            <ConfirmationDialogV9
                open={showConfirmationDialog}
                title={t('confirm')}
                content={t('unsavedChangesConfirmationMessage')}
                buttons={
                    <>
                        <DialogTrigger>
                            <Button
                                onClick={onConfirmationCancel}
                                data-analytics-button-name='confirmation_dialog_cancel'
                                aria-label={t('cancel').toString()}
                            >
                                {t('cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance='primary'
                            onClick={onConfirmationSave}
                            data-analytics-button-name='confirmation_dialog_save'
                            aria-label={t('save').toString()}
                        >
                            {t('save')}
                        </Button>
                        <Button
                            onClick={onConfirmationDiscard}
                            data-analytics-button-name='confirmation_dialog_discard'
                            aria-label={t('leave').toString()}
                        >
                            {t('leave')}
                        </Button>
                    </>
                }
            />
            <ConfirmationDialogV9
                open={showDeleteConfirmationDialog}
                title={t('deleteStatementConfirmationMessageTitle')}
                content={t('deleteStatementConfirmationMessage')}
                buttons={
                    <>
                        <DialogTrigger>
                            <Button
                                onClick={onCancelDelete}
                                data-analytics-button-name='confirmation_dialog_cancel'
                                aria-label={t('cancel').toString()}
                            >
                                {t('cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance='primary'
                            onClick={onConfirmDelete}
                            data-analytics-button-name='confirmation_dialog_save'
                            aria-label={t('delete').toString()}
                        >
                            {t('delete')}
                        </Button>
                    </>
                }
            />
        </>
    );
};

export default TableFormModal;
